import {
  ONLY_ZEROS,
  SwapOptions,
  getTokenPriceListFromCache,
  getPriceImpact,
} from './swapStateShared';
import { useContext, useEffect, useState } from 'react';
import { Pool } from '../../services/pool';
import { EstimateSwapView, PoolMode } from '../../services/swapTypes';
import {
  IEstimateSwapServerView,
  getAvgFeeFromServer,
  getPriceImpactFromServer,
  getAllPoolsFromCache,
} from '../../services/smartRouterFromServer';
import { useTokenPriceList } from '../token';
import { SwapProContext } from '../../pages/SwapPage';
import {
  toPrecision,
  percentLess,
  scientificNotationToString,
  separateRoutes,
  toNonDivisibleNumber,
  toReadableNumber,
} from '../../utils/numbers';
import { POOL_TOKEN_REFRESH_INTERVAL } from '../../services/near';
import { useIntl } from 'react-intl';
import Big from 'big.js';
import { estimateSwap } from '../../services/swap';
import { SUPPORT_LEDGER_KEY } from '../../components/swap/SwapCard';
import { swap, swapFromServer } from '../../services/swapExecution';
import { swapClmm } from '../../services/clmmPool';
import { WRAP_NEAR_CONTRACT_ID } from '../../services/wrap-near';

export const useSwap = ({
  tokenIn,
  tokenInAmount,
  setShowSwapLoading,
  tokenOut,
  slippageTolerance,
  loadingTrigger,
  setLoadingTrigger,
  loadingPause,
  reEstimateTrigger,
  supportLedger,
  tokenDeflationRateData,
}: SwapOptions) => {
  const [pool, setPool] = useState<Pool>();
  const [canSwap, setCanSwap] = useState<boolean>();
  const [estimateInOut, setEstimateInOut] = useState<string[]>([]);
  const [tokenOutAmount, setTokenOutAmount] = useState<string>('');
  const [swapError, setSwapError] = useState<Error>();
  const [swapsToDo, setSwapsToDo] = useState<EstimateSwapView[]>();
  const [swapsToDoServer, setSwapsToDoServer] =
    useState<IEstimateSwapServerView>();
  const [quoteDone, setQuoteDone] = useState<boolean>(false);

  const tokenPriceList = useTokenPriceList(loadingTrigger);

  const { enableTri } = useContext(SwapProContext);

  const [tag, setTag] = useState<any>();

  const [priceImpactValue, setPriceImpactValue] = useState<string>('0');

  const [avgFee, setAvgFee] = useState<number>(0);

  const [estimating, setEstimating] = useState<boolean>(false);

  const [count, setCount] = useState<number>(0);

  const minAmountOut = tokenOutAmount
    ? toPrecision(
        percentLess(
          slippageTolerance,
          toPrecision(tokenOutAmount, Math.min(tokenOut?.decimals ?? 8, 8))
        ),
        tokenOut?.decimals ?? 24
      )
    : null;
  const refreshTime = Number(POOL_TOKEN_REFRESH_INTERVAL) * 1000;
  const intl = useIntl();

  const setAverageFee = (estimates: EstimateSwapView[]) => {
    let avgFee: number = 0;
    try {
      const routes = separateRoutes(estimates, tokenOut.id);

      routes.forEach((route) => {
        const allocation = new Big(route[0].partialAmountIn).div(
          new Big(toNonDivisibleNumber(tokenIn.decimals, tokenInAmount))
        );

        const routeFee = route.reduce(
          (totalFee, hop) => {
            return totalFee.plus(new Big(hop.pool.fee));
          },

          new Big(0)
        );

        avgFee += allocation.mul(routeFee).toNumber();
      });
    } catch (error) {}

    setAvgFee(avgFee);
  };
  const getEstimate = async () => {
    setCanSwap(false);
    setQuoteDone(false);
    setEstimating(true);

    estimateSwap({
      tokenIn,
      tokenOut,
      amountIn: tokenInAmount,
      intl,
      supportLedger,
      loadingTrigger,
      slippage: slippageTolerance,
    })
      .then(async (estimateResult) => {
        if (estimateResult.source == 'server') {
          const { estimatesFromServer, poolsMap, tokensMap } = estimateResult;
          const { amount_out } = estimatesFromServer;
          const expectedOut = toReadableNumber(tokenOut.decimals, amount_out);
          setSwapError(null);
          setSwapsToDo(null);
          setTokenOutAmount(scientificNotationToString(expectedOut));
          setEstimateInOut([tokenInAmount, expectedOut]);
          setCanSwap(true);
          setQuoteDone(true);
          setSwapsToDoServer(estimatesFromServer);
          getAvgFeeFromServer({
            estimatesFromServer,
            setAvgFee,
            tokenInAmount,
            tokenIn,
            poolsMap,
          });
          const tokenPriceListForCal = !!tokenPriceList?.NEAR
            ? tokenPriceList
            : (await getTokenPriceListFromCache()).reduce(
                (pricesByTokenId, tokenPrice) => ({
                  ...pricesByTokenId,
                  [tokenPrice.id]: tokenPrice,
                }),
                {}
              );
          getPriceImpactFromServer({
            estimatesFromServer,
            tokenIn,
            tokenOut,
            tokenInAmount,
            tokenOutAmount: scientificNotationToString(expectedOut.toString()),
            tokenPriceList: tokenPriceListForCal,
            setPriceImpactValue,
            poolsMap,
            tokensMap,
          });
        } else if (estimateResult.source == 'script') {
          const { estimates: estimatesRes } = estimateResult;
          const estimates = estimatesRes.map((estimate) => ({
            ...estimate,
            partialAmountIn: estimate.pool.partialAmountIn,
          }));

          if (!estimates) throw '';
          if (
            localStorage.getItem(SUPPORT_LEDGER_KEY) &&
            estimates?.length > 1
          ) {
            return;
          }

          if (tokenInAmount && !ONLY_ZEROS.test(tokenInAmount)) {
            setAverageFee(estimates);
            setSwapError(null);
            setSwapsToDoServer(null);

            const expectedOut = estimates.reduce(
              (total, estimate) =>
                total.plus(
                  estimate.outputToken === tokenOut.id
                    ? estimate.estimate || 0
                    : 0
                ),
              new Big(0)
            );
            const readableOut = scientificNotationToString(
              expectedOut.toString()
            );

            setTokenOutAmount(readableOut);
            setSwapsToDo(estimates);
            setEstimateInOut([tokenInAmount, expectedOut.toString()]);
            setCanSwap(true);
            setQuoteDone(true);

            try {
              const tokenPriceListForCal = !!tokenPriceList?.NEAR
                ? tokenPriceList
                : (await getTokenPriceListFromCache()).reduce(
                    (pricesByTokenId, tokenPrice) => ({
                      ...pricesByTokenId,
                      [tokenPrice.id]: tokenPrice,
                    }),
                    {}
                  );

              setPriceImpactValue(
                getPriceImpact({
                  swapsToDo: estimates,
                  tokenIn,
                  tokenOut,
                  tokenOutAmount: readableOut,
                  tokenInAmount,
                  tokenPriceList: tokenPriceListForCal,
                })
              );
            } catch (error) {}
          }

          setPool(estimates[0].pool);
        }
      })
      .catch((error) => {
        setCanSwap(false);
        setTokenOutAmount('');
        setSwapError(error);
        setQuoteDone(true);
      })
      .finally(() => {
        setLoadingTrigger && setLoadingTrigger(false);
        setEstimating && setEstimating(false);
        setShowSwapLoading && setShowSwapLoading(false);
        setTag(`${tokenIn?.id}|${tokenOut?.id}|${tokenInAmount}`);
      });
  };
  useEffect(() => {
    getAllPoolsFromCache(true);
  }, []);
  useEffect(() => {
    if (
      tokenIn?.id &&
      tokenOut?.id &&
      tokenIn.id !== tokenOut.id &&
      Number(tokenInAmount || 0) > 0
    ) {
      getEstimate();
    } else if (ONLY_ZEROS.test(tokenInAmount || '0')) {
      setTokenOutAmount('0');
    }
  }, [
    tokenInAmount,
    reEstimateTrigger,
    tokenIn?.id,
    tokenOut?.id,
    supportLedger,
    enableTri,
    slippageTolerance,
  ]);
  useEffect(() => {
    if (
      loadingTrigger &&
      tokenIn?.id &&
      tokenOut?.id &&
      tokenIn.id !== tokenOut.id &&
      Number(tokenInAmount || 0) > 0
    ) {
      getEstimate();
    }
  }, [loadingTrigger]);
  useEffect(() => {
    let id: any = null;
    if (!loadingTrigger && !loadingPause) {
      id = setInterval(() => {
        setLoadingTrigger(true);
        setCount(count + 1);
      }, refreshTime);
    } else {
      clearInterval(id);
    }
    return () => {
      clearInterval(id);
    };
  }, [count, loadingTrigger, loadingPause]);

  const makeSwap = () => {
    const clmm = (
      swapsToDo?.[0] as {
        clmm?: Parameters<typeof swapClmm>[0] & { amountOutRaw: string };
      }
    )?.clmm;
    if (clmm) {
      const amount = Big(tokenInAmount)
        .div(Big(1).minus(tokenDeflationRateData?.rate || 0))
        .toFixed();
      const slippageBps = BigInt(Math.round((slippageTolerance || 0) * 100));
      const minOut = (
        (BigInt(clmm.amountOutRaw) * (10000n - slippageBps)) /
        10000n
      ).toString();
      swapClmm({
        contractId: clmm.contractId,
        pool: clmm.pool,
        tokenInId:
          tokenIn.symbol === 'NEAR' ? WRAP_NEAR_CONTRACT_ID : tokenIn.id,
        decimals: tokenIn.decimals,
        amount,
        quotedOut: minOut,
        sqrtPriceAfter: clmm.sqrtPriceAfter,
        slippageBps,
        wrapNear: tokenIn.symbol === 'NEAR',
      }).catch(setSwapError);
      return;
    }
    if (swapsToDo) {
      swap({
        slippageTolerance,
        swapsToDo,
        tokenIn,
        amountIn: Big(tokenInAmount)
          .div(Big(1).minus(tokenDeflationRateData?.rate || 0))
          .toFixed(),
        tokenOut,
        swapMarket: 'ref',
      }).catch(setSwapError);
    } else if (swapsToDoServer) {
      swapFromServer({
        swapsToDoServer,
        tokenIn,
        amountIn: Big(tokenInAmount)
          .div(Big(1).minus(tokenDeflationRateData?.rate || 0))
          .toFixed(),
        tokenOut,
      }).catch(setSwapError);
    }
  };
  return {
    canSwap,
    tokenOutAmount,
    minAmountOut,
    pool,
    setCanSwap,
    swapError,
    makeSwap,
    avgFee,
    tokenInAmount: swapsToDo
      ? toReadableNumber(
          tokenIn.decimals,
          swapsToDo
            .reduce(
              (total, swapTodo) => total.plus(swapTodo?.partialAmountIn || 0),
              new Big(0)
            )
            .toFixed()
        )
      : swapsToDoServer
      ? toReadableNumber(tokenIn.decimals, swapsToDoServer.amount_in || '0')
      : '1',

    pools: swapsToDo?.map((estimate) => estimate.pool),
    swapsToDo,
    isParallelSwap: swapsToDo?.every(
      (swapTodo) => swapTodo.status === PoolMode.PARALLEL
    ),
    quoteDone: quoteDone && !estimating,
    priceImpactValue: scientificNotationToString(
      new Big(priceImpactValue).minus(new Big((avgFee || 0) / 100)).toString()
    ),
    estimateInOut,
    swapsToDoServer,
    tag,
  };
};
